/**
 * EntrySplitCard — the typed card for `propose_entry_split`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Arc proposes splitting the refine drawer's scoped entry into sequential
 * parts. Part dates with a verified source quote show the quote; unverified
 * dates are flagged. Apply goes through the refine drawer's context →
 * deterministic split endpoint, which re-verifies quotes server-side and
 * auto-pins undated parts after their previous sibling. Arc never splits
 * anything itself. Outside the drawer the card renders read-only.
 */
import React, { useMemo, useState } from 'react';

import { useChronologyRefine } from './chronologyRefineContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  hint: { fontSize: 12, color: '#8a7fae', fontWeight: 400 },
  part: { display: 'flex', gap: 8, padding: '8px 12px', borderTop: '1px solid #eee6fb', alignItems: 'flex-start' },
  num: { fontWeight: 700, color: '#8a7fae', fontSize: 12, paddingTop: 2 },
  main: { flex: 1, minWidth: 0 },
  meta: { fontSize: 12, color: '#667', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  dateBadge: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#e8f0ff', color: '#1a4fc0' },
  unverified: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#fdf2cf', color: '#7a5b00' },
  undated: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#f0f0f4', color: '#778' },
  primary: { fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#e6f4ec', color: '#2c6e49' },
  quote: { fontSize: 12, color: '#556', fontStyle: 'italic', marginTop: 3 },
  rationale: { fontSize: 12, color: '#667', fontStyle: 'italic', padding: '6px 12px 0' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff', flexWrap: 'wrap' },
  btnPrimary: { padding: '5px 12px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  applied: { fontSize: 12, color: '#2c6e49', fontWeight: 600 },
  err: { color: '#c0392b', fontSize: 12 },
};

export default function EntrySplitCard({ result }) {
  const refine = useChronologyRefine();
  const proposalKey = useMemo(
    () => `split:${result?.entry_id}:${JSON.stringify(result?.parts || [])}`,
    [result],
  );
  const [error, setError] = useState('');
  const parts = result?.parts || [];

  if (result?.status !== 'ok' || parts.length < 2) {
    return (
      <div style={S.card}>
        <div style={S.head}>✨ Split not possible</div>
        <div style={{ padding: '8px 12px', color: '#778' }}>{result?.error || 'The proposal was invalid.'}</div>
      </div>
    );
  }

  const canApply = !!refine?.applySplit && refine.entryId === result.entry_id;
  const appliedIso = refine?.appliedAt?.[proposalKey];

  const apply = async () => {
    if (!canApply || refine.busy || appliedIso) return;
    setError('');
    try {
      await refine.applySplit(
        parts.map((p) => ({
          title: p.title,
          description: p.description,
          effective_date: p.effective_date ? `${String(p.effective_date).slice(0, 10)}T00:00:00Z` : null,
          date_quote: p.date_quote,
          primary: !!p.primary,
        })),
        false,
        proposalKey,
      );
    } catch (e) {
      setError(e.message || 'Split failed — the entry may have changed since this proposal.');
    }
  };

  return (
    <div style={S.card}>
      <div style={S.head}>
        <span>✨ Proposed split{result.entry_title ? ` of “${result.entry_title}”` : ''}</span>
        <span style={S.hint}>sources carry to every part; undoable</span>
      </div>
      {parts.map((p, i) => (
        <div key={i} style={S.part}>
          <span style={S.num}>{i + 1}.</span>
          <div style={S.main}>
            <strong>{p.title}</strong>
            <div style={S.meta}>
              {p.effective_date ? (
                p.date_verified
                  ? <span style={S.dateBadge}>{String(p.effective_date).slice(0, 10)} · quote verified</span>
                  : <span style={S.unverified}>{String(p.effective_date).slice(0, 10)} · quote NOT verified</span>
              ) : (
                <span style={S.undated}>undated — keeps its order after part {i || 1}</span>
              )}
              {p.primary && <span style={S.primary}>primary (keeps event link)</span>}
            </div>
            {p.description && <div style={{ marginTop: 3 }}>{p.description}</div>}
            {p.date_quote && <div style={S.quote}>“{p.date_quote}”</div>}
          </div>
        </div>
      ))}
      {result.rationale && <div style={S.rationale}>{result.rationale}</div>}
      <div style={S.actions}>
        {appliedIso ? (
          <span style={S.applied}>✓ Split applied</span>
        ) : (
          <button
            style={{ ...S.btnPrimary, ...(!canApply || refine?.busy ? { opacity: 0.55, cursor: 'default' } : null) }}
            onClick={apply}
            disabled={!canApply || refine?.busy}
            title={canApply ? 'Split this entry (undoable — Unsplit on any part)' : 'Open this entry’s Refine drawer to apply'}
          >Split into {parts.length} entries</button>
        )}
        {error && <span style={S.err}>{error}</span>}
      </div>
    </div>
  );
}
